
import Vue from 'vue';
import _trim from 'lodash/trim';
import _truncate from 'lodash/truncate';
import { mapActions, mapGetters, mapState } from 'vuex';
import { DAILY_FREE_RUNNIT_TOKENS_PER_REFERRAL, User } from '@run-diffusion/shared';
import { auth, functions, createOAuthProvider } from '@/firebase';
import type { FirebaseOAuthProvider, FirebaseUserCredential } from '@/firebase';
import { EMAIL_REGEX, SNACKBAR_STATUS } from '@/constants/constants';
import { analytics, EVENTS } from '@/services/analytics';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import PreloadModelPreview from '@/components/PreloadModelPreview.vue';
import HumanCheckDialog from '@/components/HumanCheckDialog.vue';
import {
	hasUsableHumanCheckPass,
	HumanCheckBackgroundStatus,
	isHumanCheckConfigured,
	runHumanScoreCheck,
	VerifyHumanResult,
} from '@/services/humanCheck';
import LoadingSVG from '@/assets/LoadingSVG.vue';
import AuthProviderPickers from '@/views/Login/AuthProviderPickers.vue';
import LogoMarquee from '@/views/Login/components/LogoMarquee.vue';
import LoginLandingLeftPanel from '@/views/Login/components/LoginLandingLeftPanel.vue';
import { getLoginLandingVariant } from '@/views/Login/components/loginLandingVariants';
import { getAuthProviderDisplayText } from '@/utils';
import { Route } from 'vue-router/types/router';
import { ROUTER } from '@/router/constants';
import {
	setAcceptedReferralCode,
	getPendingSsoTeamInvite,
	setPendingSsoTeamInvite,
	removePendingSsoTeamInvite,
	getSsoInviteRedirectAttempted,
	setSsoInviteRedirectAttempted,
	removeSsoInviteRedirectAttempted,
	setSsoInviteEmailMismatch,
	getSsoInviteEmailMismatch,
	removeSsoInviteEmailMismatch,
} from '@/utils/localStorage';

// Re-run the invisible Human Check this often while the page is open, so a click always finds a fresh pass.
const HUMAN_CHECK_REFRESH_MS: number = 10 * 60 * 1000;
// Every run is a billable assessment, so a page stops checking after this many (the first check plus refreshes,
// about an hour and a half of visible time). A click after that goes straight to the checkbox challenge.
const HUMAN_CHECK_MAX_RUNS: number = 10;

export default Vue.extend({
	name: 'Login',
	metaInfo: {
		title: 'Login | RunDiffusion',
	},
	data () {
		return {
			googlePopupOpen: false,
			facebookPopupOpen: false,
			microsoftPopupOpen: false,
			githubPopupOpen: false,
			ssoPopupOpen: false,

			// Human Check: an invisible check runs while this page is open; a click without a usable pass opens the dialog
			humanCheckEnabled: isHumanCheckConfigured(),
			humanCheckStatus: 'idle' as HumanCheckBackgroundStatus,
			humanCheckRunId: 0, // Also the count of runs so far, capped at HUMAN_CHECK_MAX_RUNS
			humanCheckRefreshTimer: null as ReturnType<typeof setInterval> | null,
			humanCheckDialogOpen: false,
			humanCheckAutoContinue: false,
			humanCheckPendingAction: null as (() => void) | null,

			email: null,
			password: null,
			confirmPassword: null,
			loadingEmailPasswordAuth: false,
			loadingTeamSignUp: false,
			alreadySignedUp: false,
			signUpTeamMember: null,
			teamName: null,

			// Enforced-SSO team-invite auto-login
			enforcedSsoProvider: null,
			signUpInviteCode: null,
			redirectingToSso: false,
			ssoRedirectReturned: false,
			ssoEmailMismatch: false,
			showPassword1: false,
			showPassword2: false,
			showPassword3: false,

			referredByCode: null,
			DAILY_FREE_RUNNIT_TOKENS_PER_REFERRAL,

			windowView: 0,
			SIGN_IN_WINDOW_ITEM: 0,
			SIGN_UP_WINDOW_ITEM: 1,
			FORGOT_PASSWORD_WINDOW_ITEM: 2,

			// FORM
			formValid: true,
			rules: {
				required: [
					v => !!_trim(v) || 'Required',
				],
				email: [
					v => !!_trim(v) || 'Email is required',
					v => EMAIL_REGEX.test(_trim(v)) || 'Incorrect email format',
				],
			},
		};
	},
	async mounted () {
		this.startHumanCheck();

		// Process referral code from URL if present
		this.checkForReferralCode();

		// Capture redirect-based SSO sign-ins for the signup funnel. signInWithRedirect (used by the
		// enforced-SSO invite flow and automaticSsoLogin) never returns a credential inline, so this is
		// the only place additionalUserInfo.isNewUser is available to fire SIGNUP_SUCCEEDED.
		this.trackSsoRedirectSignup();

		if (this.isNewTeamMemberSignupFlow) {
			await this.getAndLoadTeamSignUp();
		} else if (this.isSignupFlow || this.$route.query.signup === 'true') {
			this.windowView = this.SIGN_UP_WINDOW_ITEM;
		} else {
			if (this.shouldDefaultToSignUpFromVariant) {
				this.windowView = this.SIGN_UP_WINDOW_ITEM;
			}

			await this.automaticSsoLogin();
		}
	},
	beforeDestroy () {
		this.stopHumanCheckRefresh();
	},
	watch: {
		user (newVal: User, oldVal: User) {
			if (
				newVal &&
				newVal !== oldVal &&
				(
					!this.isNewTeamMemberSignupFlow ||
					!this.loadingTeamSignUp
				)
			) {
				this.routeAwayAfterSuccessfulLogin(newVal);
			}
		},
		'$route' (newVal: Route) {
			if (
				newVal.name === ROUTER.TEAM_SIGNUP &&
				newVal.query &&
				newVal.query.teamName &&
				!this.teamName
			) {
				this.teamName = decodeURIComponent(newVal.query.teamName as string);
			}
		},
	},
	computed: {
		...mapGetters([
			'isLoggingIn',
		]),
		...mapState([
			'user',
			'appQueryParamsData',
			'loginQueryParamsData',
		]),
		isNewTeamMemberSignupFlow () {
			return this.$route.name === ROUTER.TEAM_SIGNUP;
		},
		isSignupFlow () {
			return this.$route.name === ROUTER.SIGNUP;
		},
		loginVariantKey (): string | null {
			const raw = (this.$route && this.$route.query && (this.$route.query as any).loginVariant) || null;
			if (Array.isArray(raw)) return raw[0] || null;
			return typeof raw === 'string' ? raw : null;
		},
		activeLoginLandingVariant () {
			return getLoginLandingVariant(this.loginVariantKey);
		},
		signUpButtonLabel (): string {
			return (this.activeLoginLandingVariant && this.activeLoginLandingVariant.signUpCtaText) || 'Sign Up';
		},
		shouldDefaultToSignUpFromVariant (): boolean {
			return !!(
				this.$route &&
				this.$route.name === ROUTER.LOGIN &&
				this.activeLoginLandingVariant &&
				this.activeLoginLandingVariant.defaultWindowView === 'signup'
			);
		},
		passwordsMatch () {
			return this.password === this.confirmPassword;
		},
		logInDisabled () {
			return !!(
				!this.email ||
				!this.password ||
				this.loadingEmailPasswordAuth ||
				this.loadingTeamSignUp
			);
		},
		signUpDisabled () {
			return !!(
				!this.email ||
				!this.password ||
				!this.confirmPassword ||
				this.alreadySignedUp ||
				!this.passwordsMatch ||
				this.loadingEmailPasswordAuth ||
				this.loadingTeamSignUp
			);
		},
		authProviderSignUpDisabled () {
			return !!(
				this.alreadySignedUp ||
				this.loadingEmailPasswordAuth ||
				this.loadingTeamSignUp
			);
		},
		passwordResetDisabled () {
			return (
				!this.email ||
				!EMAIL_REGEX.test(_trim(this.email))
			);
		},
	},
	methods: {
		...mapActions([
			'setAuthUserCredential',
			'updateSnackbar',
			'updateLoginQueryParamsData',
		]),
		startHumanCheck () {
			if (!this.humanCheckEnabled) return;
			this.runBackgroundHumanCheck();
			// Each run is a billable assessment, so a tab left open in the background stops refreshing and catches up
			// when the person comes back to it.
			this.humanCheckRefreshTimer = setInterval(() => {
				if (!document.hidden && this.humanCheckStatus !== 'pending' && !this.humanCheckDialogOpen) {
					this.runBackgroundHumanCheck();
				}
			}, HUMAN_CHECK_REFRESH_MS);
			document.addEventListener('visibilitychange', this.onHumanCheckVisibilityChange);
		},
		onHumanCheckVisibilityChange () {
			if (
				!document.hidden &&
				this.humanCheckStatus !== 'pending' &&
				!this.humanCheckDialogOpen &&
				!hasUsableHumanCheckPass()
			) {
				this.runBackgroundHumanCheck();
			}
		},
		stopHumanCheckRefresh () {
			if (this.humanCheckRefreshTimer) {
				clearInterval(this.humanCheckRefreshTimer);
				this.humanCheckRefreshTimer = null;
			}
			document.removeEventListener('visibilitychange', this.onHumanCheckVisibilityChange);
		},
		async runBackgroundHumanCheck () {
			if (this.humanCheckRunId >= HUMAN_CHECK_MAX_RUNS) {
				this.stopHumanCheckRefresh();
				return;
			}
			const runId: number = ++this.humanCheckRunId;
			if (runId >= HUMAN_CHECK_MAX_RUNS) {
				// This is the last one this page gets
				this.stopHumanCheckRefresh();
			}
			this.humanCheckStatus = 'pending';
			const result: VerifyHumanResult = await runHumanScoreCheck();
			if (runId !== this.humanCheckRunId) return; // A newer run superseded this one
			this.humanCheckStatus = result === 'passed' ? 'passed' : 'failed';
		},
		// Runs `action` straight away when a usable pass is stored, so an OAuth popup it opens still counts as part
		// of the click. Otherwise opens the Human Check dialog and runs it from the dialog's Continue instead.
		// `needsGesture` is for actions that open a popup; the rest continue as soon as the check clears.
		withHumanCheck (action: () => void, needsGesture: boolean) {
			if (!this.humanCheckEnabled || hasUsableHumanCheckPass()) {
				action();
				return;
			}
			if (this.humanCheckStatus === 'passed') {
				// The pass went stale (e.g. the laptop slept through the refresh), so get a new one. Once the page has
				// used up its runs this is a no-op, and the dialog goes straight to the checkbox challenge.
				this.runBackgroundHumanCheck();
			}
			this.humanCheckPendingAction = action;
			this.humanCheckAutoContinue = !needsGesture;
			this.humanCheckDialogOpen = true;
		},
		onHumanCheckContinue () {
			const action: (() => void) | null = this.humanCheckPendingAction;
			this.humanCheckDialogOpen = false;
			this.humanCheckPendingAction = null;
			if (action) action();
		},
		onHumanCheckDialogInput (open: boolean) {
			if (!open) this.humanCheckPendingAction = null;
		},
		checkForReferralCode () {
			// Get the referral code from the URL
			const urlParams = new URLSearchParams(window.location.search);
			const referredByCode: string = urlParams.get('referralCode') || null;
			this.referredByCode = referredByCode || null;

			if (referredByCode) {
				// Store the referral code in the browser
				setAcceptedReferralCode(referredByCode);

				// Auto-switch to sign up view if this is a valid referral
				if (this.$route.name === ROUTER.LOGIN) {
					this.windowView = this.SIGN_UP_WINDOW_ITEM;
				}
			}
		},
		async automaticSsoLogin () {
			const { autoLoginSsoProvider } = this.loginQueryParamsData;
			if (autoLoginSsoProvider) {
				const providerId: string = `saml.${autoLoginSsoProvider.toLowerCase().replace(/^saml\./, '')}`;
				try {
					await this.logInWithThirdPartyAuthProvider(providerId, false);
				} catch (error) {
					this.onThirdPartyAuthError(providerId, error.code, error.message);
				}
			}
		},
		onThirdPartyAuthError (providerId: string, errorCode: string, errorMessage: string) {
			console.log({
				errorCode,
				errorMessage,
			});

			let message: string = 'Oops! Error signing up, try again.';
			if (errorCode === 'auth/popup-closed-by-user') {
				message = `The ${getAuthProviderDisplayText(providerId, false)} popup has been closed by the user before finalizing the operation.`;
			} else if (errorCode === 'auth/account-exists-with-different-credential') {
				message = `An account already exists with this same email but using a different login method. Sign in using the login method associated with this email address.`;
			} else if (errorCode === 'auth/popup-blocked') {
				message = 'Unable to establish a connection with the popup. It may have been blocked by the browser.';
			} else if (errorCode === 'auth/operation-not-allowed') {
				message = 'The identity provider configuration is not found.';
			} else if (errorCode === 'auth/cancelled-popup-request') {
				return;
			} else if (errorCode) {
				message = errorMessage;
			}

			this.updateSnackbar({
				status: SNACKBAR_STATUS.ERROR,
				message,
				show: true,
				timeout: 20000,
			});
		},
		async logInWithThirdPartyAuthProvider (providerId: string, doPopup: boolean) {
			const provider: FirebaseOAuthProvider = createOAuthProvider(providerId);
			provider.addScope('email');
			provider.addScope(providerId === 'facebook.com' ? 'public_profile' : 'profile');

			if (doPopup) {
				const userCredential: FirebaseUserCredential = await auth.signInWithPopup(provider);
				await this.setAuthUserCredential(userCredential);

				// additionalUserInfo.isNewUser distinguishes a brand-new signup from a
				// returning login — only fire signup_succeeded for genuinely new users.
				if (this._get(userCredential, 'additionalUserInfo.isNewUser', false)) {
					analytics.track(EVENTS.SIGNUP_SUCCEEDED, {
						auth_method: providerId,
						is_team_member_signup: !!this.isNewTeamMemberSignupFlow,
						was_referred: !!this.referredByCode,
					});
				}

				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Login Successful! ${userCredential.user.email}`,
					show: true,
					timeout: 5000,
				});
			} else {
				// Do redirect cuz popups get blocked if invoked programmatically
				await auth.signInWithRedirect(provider);
			}
		},
		continueWithSsoAuth (providerId: string) {
			if (this.authProviderSignUpDisabled || !providerId) {
				return;
			}
			this.withHumanCheck(() => { this.doContinueWithSsoAuth(providerId); }, true);
		},
		async doContinueWithSsoAuth (providerId: string) {
			this.ssoPopupOpen = true;

			try {
				await this.logInWithThirdPartyAuthProvider(providerId, true);
			} catch (error) {
				this.onThirdPartyAuthError(providerId, error.code, error.message);
			} finally {
				this.ssoPopupOpen = false;
			}
		},
		continueWithGoogleAuth () {
			if (this.authProviderSignUpDisabled) return;
			this.withHumanCheck(() => { this.doContinueWithGoogleAuth(); }, true);
		},
		async doContinueWithGoogleAuth () {
			this.googlePopupOpen = true;
			const providerId: string = 'google.com';

			try {
				await this.logInWithThirdPartyAuthProvider(providerId, true);
			} catch (error) {
				this.onThirdPartyAuthError(providerId, error.code, error.message);
			} finally {
				this.googlePopupOpen = false;
			}
		},
		continueWithFacebookAuth () {
			if (this.authProviderSignUpDisabled) return;
			this.withHumanCheck(() => { this.doContinueWithFacebookAuth(); }, true);
		},
		async doContinueWithFacebookAuth () {
			this.facebookPopupOpen = true;
			const providerId: string = 'facebook.com';

			try {
				await this.logInWithThirdPartyAuthProvider(providerId, true);
			} catch (error) {
				this.onThirdPartyAuthError(providerId, error.code, error.message);
			} finally {
				this.facebookPopupOpen = false;
			}
		},
		continueWithMicrosoftAuth () {
			if (this.authProviderSignUpDisabled) return;
			this.withHumanCheck(() => { this.doContinueWithMicrosoftAuth(); }, true);
		},
		async doContinueWithMicrosoftAuth () {
			this.microsoftPopupOpen = true;
			const providerId: string = 'microsoft.com';

			try {
				await this.logInWithThirdPartyAuthProvider(providerId, true);
			} catch (error) {
				this.onThirdPartyAuthError(providerId, error.code, error.message);
			} finally {
				this.microsoftPopupOpen = false;
			}
		},
		continueWithGithubAuth () {
			if (this.authProviderSignUpDisabled) return;
			this.withHumanCheck(() => { this.doContinueWithGithubAuth(); }, true);
		},
		async doContinueWithGithubAuth () {
			this.githubPopupOpen = true;
			const providerId: string = 'github.com';

			try {
				await this.logInWithThirdPartyAuthProvider(providerId, true);
			} catch (error) {
				this.onThirdPartyAuthError(providerId, error.code, error.message);
			} finally {
				this.githubPopupOpen = false;
			}
		},
		getTruncatePassword () {
			// Passwords just aren't this long. Prevent security attacks.
			return _truncate(this.password, { length: 1000 });
		},
		logIn () {
			if (this.logInDisabled) return;
			this.withHumanCheck(() => { this.doLogIn(); }, false);
		},
		async doLogIn () {
			this.loadingEmailPasswordAuth = true;

			try {
				const userCredential: FirebaseUserCredential = await auth.signInWithEmailAndPassword(
					_trim(this.email).toLowerCase(),
					this.getTruncatePassword(),
				);
				await this.setAuthUserCredential(userCredential);
			} catch (error) {
				const errorCode = error.code;
				const errorMessage = error.message;

				console.log({
					errorCode,
					errorMessage,
				});

				let message: string = 'Oops! Error logging in, try again.';
				if (errorCode === 'auth/user-not-found') {
					message = 'User not found';
				} else if (errorCode === 'auth/wrong-password') {
					message = 'The password is incorrect or you signed up using a different login method';
				} else if (errorCode === 'auth/user-disabled') {
					message = 'User is disabled';
				}

				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message,
					show: true,
					timeout: 10000,
				});
			} finally {
				this.loadingEmailPasswordAuth = false;
			}
		},
		signUp () {
			if (this.signUpDisabled) return;
			this.withHumanCheck(() => { this.doSignUp(); }, false);
		},
		async doSignUp () {
			this.loadingEmailPasswordAuth = true;

			try {
				const userCredential: FirebaseUserCredential = await auth.createUserWithEmailAndPassword(
					_trim(this.email).toLowerCase(),
					this.getTruncatePassword(),
				);
				await this.setAuthUserCredential(userCredential);
				analytics.track(EVENTS.SIGNUP_SUCCEEDED, {
					auth_method: 'email_password',
					is_team_member_signup: !!this.isNewTeamMemberSignupFlow,
					was_referred: !!this.referredByCode,
				});
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: 'New account created!',
					show: true,
				});
			} catch (error) {
				const errorCode = error.code;
				const errorMessage = error.message;

				console.log({
					errorCode,
					errorMessage,
				});

				let message: string = 'Oops! Error signing up, try again.';
				if (errorCode === 'auth/email-already-in-use') {
					message = 'Email already in use';
				} else if (errorCode === 'auth/weak-password') {
					message = 'That password is too weak, choose a stronger password';
				}

				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message,
					show: true,
					timeout: 10000,
				});
			} finally {
				this.loadingEmailPasswordAuth = false;
			}
		},
		handleForgotPassword () {
			if (!_trim(this.email)) {
				this.$refs.passwordResetEmail?.focus();
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Please first provide email',
					show: true,
				});
				return;
			}
			this.withHumanCheck(() => { this.sendPasswordReset(); }, false);
		},
		async sendPasswordReset () {
			const trimmedEmail: string = _trim(this.email).toLowerCase();
			if (trimmedEmail) {
				try {
					const providerIds: string[] = await auth.fetchSignInMethodsForEmail(trimmedEmail);
					if (!providerIds.length || providerIds.includes('password')) {
						await auth.sendPasswordResetEmail(_trim(this.email));

						this.updateSnackbar({
							status: SNACKBAR_STATUS.SUCCESS,
							message: 'Password reset sent, check your email!',
							show: true,
							timeout: 10000,
						});
					} else {
						this.updateSnackbar({
							status: SNACKBAR_STATUS.ERROR,
							message: `Your account does not have Email & Password sign-in credentials. Sign in using the ${providerIds[0]} credentials associated with this email address.`,
							show: true,
							timeout: 10000,
						});
					}
				} catch (error) {
					const errorCode = error.code;
					const errorMessage = error.message;

					console.log({
						errorCode,
						errorMessage,
					});

					let message: string = 'Uh oh! Error sending password reset, try again';
					if (errorCode === 'auth/user-not-found') {
						message = 'User not found';
					} else if (errorCode === 'auth/user-disabled') {
						message = 'User is disabled';
					}
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message,
						show: true,
					});
				}
			}
		},
		async getAndLoadTeamSignUp () {
			const { query } = this.$route;
			// Fall back to the persisted Invite Context if the query string was dropped across the
			// SSO redirect round-trip, so the invite can still be validated + completed on return.
			const persistedInvite = getPendingSsoTeamInvite();
			const teamId: string = this._get(query, 'teamId', null) || (persistedInvite && persistedInvite.teamId) || null;
			const inviteCode: string = this._get(query, 'inviteCode', null) || (persistedInvite && persistedInvite.inviteCode) || null;
			const isExistingUser: string = this._get(query, 'isExistingUser', null);

			if (!teamId || !inviteCode) return;

			this.windowView = isExistingUser ? this.SIGN_IN_WINDOW_ITEM : this.SIGN_UP_WINDOW_ITEM;

			const onError: Function = (e) => {
				console.error('Error loading up team member to sign up: ', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Problem with loading team to sign up, please reach out to report issues by going to Resources -> Help from the menu options',
					show: true,
					timeout: 10000,
				});
			};

			this.loadingTeamSignUp = true;
			try {
				const functionRef = functions
					.httpsCallable('getTeamMemberByInviteCode');
				const { success, alreadySignedUp, teamMember, teamName, enforceSso, ssoProvider } = (await functionRef({
					teamId,
					inviteCode,
				})).data;

				if (success && !teamMember) {
					this.resetSignedUpAndGoToRegularLogin();
				} else if (success) {
					this.signUpTeamMember = teamMember;
					this.signUpInviteCode = inviteCode;
					this.teamName = teamName;
					this.email = teamMember.email;
					this.alreadySignedUp = alreadySignedUp;

					if (this.alreadySignedUp) return;

					this.updateLoginQueryParamsData({
						teamId,
						inviteCode,
					});
					if (this.user) {
						this.routeAwayAfterSuccessfulLogin(this.user);
						return;
					}

					// Enforced-SSO invite auto-login: an unauthenticated invitee whose team enforces SSO
					// can only sign in one way, so skip the full login menu and go straight to SSO.
					if (enforceSso && ssoProvider) {
						this.enforcedSsoProvider = ssoProvider;
						this.redirectingToSso = true;

						if (getSsoInviteEmailMismatch() === inviteCode) {
							// We came back from SSO with an account whose email != the invited address.
							// Explain it (mismatch interstitial) instead of silently bouncing them back in.
							this.ssoEmailMismatch = true;
							this.ssoRedirectReturned = true;
						} else if (getSsoInviteRedirectAttempted() === inviteCode) {
							// We just returned from the SAML provider. If auth succeeded, the `user`
							// watcher routes away; if it was cancelled, the interstitial's manual
							// "Continue with SSO" button lets them retry — but never auto-re-fire (loop guard).
							this.ssoRedirectReturned = true;
						} else {
							// First landing: persist the Invite Context to survive the full-page redirect.
							setPendingSsoTeamInvite({ teamId, inviteCode, ssoProvider });
							setSsoInviteRedirectAttempted(inviteCode);
							if (getSsoInviteRedirectAttempted() !== inviteCode) {
								// sessionStorage is unavailable (blocked / throwing — strict cookie policy,
								// some embedded webviews), so the one-shot loop guard can't survive the
								// redirect. Auto-firing now would risk an infinite bounce, so require a
								// manual click instead.
								this.ssoRedirectReturned = true;
							} else {
								await this.continueWithEnforcedSso();
							}
						}
					}
				} else {
					onError(new Error('getTeamMemberByInviteCode did not return success'));
				}
			} catch (e) {
				onError(e);
			} finally {
				this.loadingTeamSignUp = false;
			}
		},
		async continueWithEnforcedSso () {
			const ssoProvider: string = this.enforcedSsoProvider;
			if (!ssoProvider) return;
			const providerId: string = `saml.${ssoProvider.toLowerCase().replace(/^saml\./, '')}`;
			// Mark this invite as attempted BEFORE leaving the page so a cancelled return won't auto-re-fire.
			setSsoInviteRedirectAttempted(this.signUpInviteCode);
			try {
				await this.logInWithThirdPartyAuthProvider(providerId, false);
			} catch (error) {
				this.ssoRedirectReturned = true;
				this.onThirdPartyAuthError(providerId, error.code, error.message);
			}
		},
		async retryEnforcedSsoWithDifferentAccount () {
			// Deliberate retry from the email-mismatch interstitial: clear the mismatch marker so a
			// successful match this time routes through, then re-initiate the SSO redirect.
			removeSsoInviteEmailMismatch();
			this.ssoEmailMismatch = false;
			await this.continueWithEnforcedSso();
		},
		async trackSsoRedirectSignup () {
			try {
				const userCredential: FirebaseUserCredential = await auth.getRedirectResult();
				if (
					userCredential &&
					userCredential.user &&
					this._get(userCredential, 'additionalUserInfo.isNewUser', false)
				) {
					analytics.track(EVENTS.SIGNUP_SUCCEEDED, {
						auth_method: this._get(userCredential, 'additionalUserInfo.providerId', null) || 'sso',
						is_team_member_signup: !!this.isNewTeamMemberSignupFlow,
						was_referred: !!this.referredByCode,
					});
				}
			} catch (e) {
				// getRedirectResult can reject (e.g. account-exists-with-different-credential). This is
				// analytics-only and the auth error surfaces elsewhere, so just swallow it here.
				console.error('getRedirectResult signup tracking error', e);
			}
		},
		routeAwayAfterSuccessfulLogin (user: User) {
			if (this.isNewTeamMemberSignupFlow) {
				// Compare against the invited address case-insensitively so a pure casing/whitespace
				// difference (common with SAML-asserted emails) is NOT treated as a wrong account.
				const invitedEmail: string = this._get(this.signUpTeamMember, 'email', null);
				const emailMatches: boolean = !invitedEmail || (
					!!user.email && _trim(user.email).toLowerCase() === _trim(invitedEmail).toLowerCase()
				);
				if (this.signUpTeamMember && !emailMatches) {
					// Signed in with an account that doesn't match the invite. Record it so the re-land
					// explains the problem instead of silently bouncing back into the same failing SSO.
					if (this.signUpInviteCode) {
						setSsoInviteEmailMismatch(this.signUpInviteCode);
					}
					this.$emit('logout', true);
				} else {
					// Pending enforced-SSO invite has been honored — clear all transient guards.
					removePendingSsoTeamInvite();
					removeSsoInviteRedirectAttempted();
					removeSsoInviteEmailMismatch();
					this.routerReplace(this.$route, this.$router, {
						name: ROUTER.COMPANY_DASHBOARD,
						query: {
							finishedConnectingTeam: '1',
						},
					});
				}
			} else {
				this.routerReplace(this.$route, this.$router, this.$route.query.redirect || { name: ROUTER.SESSIONS });
			}
		},
		resetSignedUpAndGoToTeamSignupLogin () {
			this.alreadySignedUp = false;
			this.routerReplace(this.$route, this.$router, { 'query': null });
			this.windowView = this.SIGN_IN_WINDOW_ITEM;
		},
		resetSignedUpAndGoToRegularLogin () {
			// Invite is gone/invalid — drop any enforced-SSO guards so they don't pollute the tab session.
			removePendingSsoTeamInvite();
			removeSsoInviteRedirectAttempted();
			removeSsoInviteEmailMismatch();
			this.alreadySignedUp = false;
			this.routerReplace(this.$route, this.$router, { name: ROUTER.LOGIN });
			this.windowView = this.SIGN_IN_WINDOW_ITEM;
		},
	},
	components: {
		AuthProviderPickers,
		LoadingSVG,
		HumanCheckDialog,
		ConfirmDialog,
		PreloadModelPreview,
		LogoMarquee,
		LoginLandingLeftPanel,
	},
});
